<template>
  <div class="acs">
    <section class="acs__kpis" data-tour="admin-cs-kpis">
      <article class="sui-card acs__kpi" :class="{ 'is-alert': unansweredQna > 0 }">
        <span class="acs__kpi-label">미답변 상품문의</span>
        <span class="acs__kpi-value">{{ unansweredQna }}<em>건</em></span>
      </article>
      <article class="sui-card acs__kpi" :class="{ 'is-alert': unansweredReview > 0 }">
        <span class="acs__kpi-label">미답변 후기</span>
        <span class="acs__kpi-value">{{ unansweredReview }}<em>건</em></span>
      </article>
      <article class="sui-card acs__kpi" :class="{ 'is-alert': unansweredConsult > 0 }">
        <span class="acs__kpi-label">미답변 1:1 상담</span>
        <span class="acs__kpi-value">{{ unansweredConsult }}<em>건</em></span>
      </article>
      <article class="sui-card acs__kpi">
        <span class="acs__kpi-label">조회 건수</span>
        <span class="acs__kpi-value">{{ viewCount }}<em>건</em></span>
      </article>
    </section>

    <section class="sui-card sui-card--flush" data-tour="admin-cs-list">
      <div class="sui-tabs" data-tour="admin-cs-tabs">
        <button
          type="button"
          class="sui-tab"
          :class="{ 'is-active': tab === 'consult' }"
          @click="setTab('consult')"
        >
          1:1 상담 <em>{{ unansweredConsult }}</em>
        </button>
        <button
          type="button"
          class="sui-tab"
          :class="{ 'is-active': tab === 'qna' }"
          @click="setTab('qna')"
        >
          상품 문의 <em>{{ unansweredQna }}</em>
        </button>
        <button
          type="button"
          class="sui-tab"
          :class="{ 'is-active': tab === 'review' }"
          @click="setTab('review')"
        >
          구매후기 <em>{{ unansweredReview }}</em>
        </button>
      </div>

      <div class="sui-toolbar" data-tour="admin-cs-toolbar">
        <template v-if="tab === 'consult'">
          <!-- 카이온 홍보메일 신청(PROMO)은 이 탭에 없다. 서버가 목록에서 빼며, 카이온 상담 화면에서만 본다. -->
          <div class="acs__channel-tabs" data-tour="admin-cs-channel" role="tablist" aria-label="상담 채널">
            <button
              type="button"
              role="tab"
              class="acs__channel-tab"
              :class="{ 'is-active': consultChannel === '' }"
              :aria-selected="consultChannel === ''"
              @click="setConsultChannel('')"
            >
              전체
            </button>
            <button
              type="button"
              role="tab"
              class="acs__channel-tab"
              :class="{ 'is-active': consultChannel === 'PORTAL' }"
              :aria-selected="consultChannel === 'PORTAL'"
              @click="setConsultChannel('PORTAL')"
            >
              포털 상담 <em v-if="unansweredConsultPortal">({{ unansweredConsultPortal }})</em>
            </button>
            <button
              type="button"
              role="tab"
              class="acs__channel-tab"
              :class="{ 'is-active': consultChannel === 'MALL' }"
              :aria-selected="consultChannel === 'MALL'"
              @click="setConsultChannel('MALL')"
            >
              몰 문의 <em v-if="unansweredConsultMall">({{ unansweredConsultMall }})</em>
            </button>
          </div>
          <label class="acs__select">
            <span class="acs__sr">상태</span>
            <select v-model="consultStatus" @change="loadConsult(1)">
              <option value="">전체</option>
              <option value="WAIT">답변대기</option>
              <option value="DONE">답변완료</option>
              <option value="DELEGATED">판매자 위임</option>
            </select>
          </label>
          <label class="sui-search">
            <AppIcon icon="mdi-magnify" :size="17" />
            <input
              v-model.trim="keyword"
              type="search"
              placeholder="제목 · ID · 기관명 · 기관기호 · 주문번호"
              @keydown.enter="loadConsult(1)"
            />
          </label>
        </template>
        <template v-else>
          <label class="sui-check">
            <input v-model="unansweredOnly" type="checkbox" @change="load" />
            <span>미답변만</span>
          </label>
          <label class="sui-search">
            <AppIcon icon="mdi-magnify" :size="17" />
            <input
              v-model.trim="keyword"
              type="search"
              placeholder="상품 · 업체 · 기관 · 주문번호 · 내용 검색"
              @keydown.enter="load"
            />
          </label>
        </template>
        <button
          type="button"
          class="sui-btn"
          :disabled="loading"
          @click="tab === 'consult' ? loadConsult(1) : load()"
        >
          <AppIcon icon="mdi-magnify" :size="16" />
          검색
        </button>
        <span class="sui-toolbar__spacer" />
        <button
          type="button"
          class="sui-btn"
          :disabled="loading"
          @click="tab === 'consult' ? loadConsult() : load()"
        >
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>

      <p v-if="errorMessage" class="acs__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>
      <p v-if="toast" class="acs__toast">
        <AppIcon icon="mdi-check-circle" :size="16" />
        {{ toast }}
      </p>

      <div v-if="tab === 'consult' && consultItems.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>상태</th>
              <th>분류</th>
              <th>작성자 ID</th>
              <th>제목</th>
              <th>관련 주문</th>
              <th>기관</th>
              <th>연락처</th>
              <th class="is-center">처리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in consultItems" :key="row.id">
              <td class="acs__status-cell">
                <span
                  class="sui-badge"
                  :class="consultStatusBadgeClass(row)"
                  :title="
                    row.sellerReplyAllowed
                      ? '관리자가 판매업체에게 답변 권한을 위임한 상담'
                      : undefined
                  "
                >
                  {{ consultStatusLabel(row) }}
                </span>
                <span class="sui-table__sub">{{ formatDate(row.createdAt) }}</span>
              </td>
              <td>{{ row.kind || '일반상담' }}</td>
              <td>
                <span class="sui-table__strong">{{ row.authorId }}</span>
              </td>
              <td>
                <span class="acs__body">{{ row.title }}</span>
                <span
                  class="acs__channel-badge"
                  :class="row.channel === 'MALL' ? 'is-mall' : 'is-portal'"
                >
                  {{ row.channel === 'MALL' ? '몰' : '포털' }}
                </span>
                <span v-if="row.isTest" class="acs__channel-badge is-test">테스트</span>
              </td>
              <td>
                <router-link
                  v-if="row.orderCode"
                  class="acs__order-badge"
                  :to="{ path: '/admin/orders', query: { keyword: row.orderCode } }"
                  :title="`${row.orderCode} 주문 상세로 이동`"
                >
                  <AppIcon icon="mdi-receipt-text-outline" :size="14" />
                  <span class="acs__order-badge-code">{{ row.orderCode }}</span>
                  <AppIcon icon="mdi-chevron-right" :size="14" />
                </router-link>
                <span v-else class="acs__order-badge acs__order-badge--none">
                  <AppIcon icon="mdi-help-circle-outline" :size="14" />
                  일반 상담
                </span>
              </td>
              <td>
                <span>{{ row.orgName || '-' }}</span>
                <span class="sui-table__sub">{{ row.orgCode || '-' }}</span>
              </td>
              <td>{{ row.contactPhone || '-' }}</td>
              <td class="is-center">
                <button type="button" class="sui-btn sui-btn--sm" @click="openConsultDetail(row.id)">
                  상세·답변
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="consultPagination.totalPages > 1" class="acs__pager">
          <button
            type="button"
            class="sui-btn sui-btn--sm"
            :disabled="consultPagination.page <= 1 || loading"
            @click="loadConsult(consultPagination.page - 1)"
          >
            이전
          </button>
          <span>{{ consultPagination.page }} / {{ consultPagination.totalPages }}</span>
          <button
            type="button"
            class="sui-btn sui-btn--sm"
            :disabled="consultPagination.page >= consultPagination.totalPages || loading"
            @click="loadConsult(consultPagination.page + 1)"
          >
            다음
          </button>
        </div>
      </div>

      <div v-else-if="tab === 'qna' && qnas.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>상태</th>
              <th>상품 · 판매업체</th>
              <th>기관</th>
              <th>관련 주문</th>
              <th>문의</th>
              <th class="is-center">처리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in qnas" :key="row.id">
              <td>
                <span class="sui-badge" :class="row.answer ? 'sui-badge--green' : 'sui-badge--amber'">
                  {{ row.answer ? '답변완료' : '미답변' }}
                </span>
                <span class="sui-table__sub">{{ row.date }}</span>
              </td>
              <td>
                <span class="sui-table__strong">{{ row.productName || row.productCode }}</span>
                <span class="sui-table__sub">{{ row.storeName || '-' }}</span>
              </td>
              <td>
                <span>{{ row.org }}</span>
                <span class="sui-table__sub">{{ row.author }}</span>
              </td>
              <td>
                <router-link
                  v-if="row.orderCode"
                  class="acs__order-badge"
                  :to="{ path: '/admin/orders', query: { keyword: row.orderCode } }"
                  :title="`${row.orderCode} 주문 상세로 이동`"
                >
                  <AppIcon icon="mdi-receipt-text-outline" :size="14" />
                  <span class="acs__order-badge-code">{{ row.orderCode }}</span>
                  <AppIcon icon="mdi-chevron-right" :size="14" />
                </router-link>
                <span v-else class="acs__order-badge acs__order-badge--none">
                  <AppIcon icon="mdi-help-circle-outline" :size="14" />
                  일반 문의
                </span>
                <span v-if="row.orderAt" class="sui-table__sub">
                  {{ String(row.orderAt).slice(0, 10) }}
                </span>
                <span v-if="row.orderSummary" class="sui-table__sub acs__order-summary" :title="row.orderSummary">
                  {{ row.orderSummary }}
                </span>
              </td>
              <td>
                <span class="acs__body">{{ row.question }}</span>
                <span v-if="row.answer" class="sui-table__sub acs__reply">A. {{ row.answer }}</span>
              </td>
              <td class="is-center">
                <button
                  v-if="!row.answer"
                  type="button"
                  class="sui-btn sui-btn--sm sui-btn--primary"
                  @click="openQnaReply(row)"
                >
                  답변
                </button>
                <a
                  class="sui-btn sui-btn--sm"
                  :href="`/mall/${encodeURIComponent(row.productCode)}`"
                  target="_blank"
                  rel="noopener"
                >
                  상품
                </a>
                <button
                  type="button"
                  class="sui-btn sui-btn--sm"
                  :disabled="working === row.id"
                  @click="onHide('qna', row.idx, row.id)"
                >
                  숨김
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="tab === 'review' && reviews.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>상태</th>
              <th>상품 · 판매업체</th>
              <th>기관 · 별점</th>
              <th>관련 주문</th>
              <th>내용</th>
              <th class="is-center">처리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in reviews" :key="row.id">
              <td>
                <span class="sui-badge" :class="row.reply ? 'sui-badge--green' : 'sui-badge--amber'">
                  {{ row.reply ? '답변완료' : '미답변' }}
                </span>
                <span class="sui-table__sub">{{ row.date }}</span>
              </td>
              <td>
                <span class="sui-table__strong">{{ row.productName || row.productCode }}</span>
                <span class="sui-table__sub">{{ row.storeName || '-' }}</span>
              </td>
              <td>
                <span>{{ row.org }}</span>
                <span class="sui-table__sub">★ {{ row.rating }} · {{ row.author }}</span>
              </td>
              <td>
                <router-link
                  v-if="row.orderCode"
                  class="acs__order-badge"
                  :to="{ path: '/admin/orders', query: { keyword: row.orderCode } }"
                  :title="`${row.orderCode} 주문 상세로 이동`"
                >
                  <AppIcon icon="mdi-receipt-text-outline" :size="14" />
                  <span class="acs__order-badge-code">{{ row.orderCode }}</span>
                  <AppIcon icon="mdi-chevron-right" :size="14" />
                </router-link>
                <span v-else class="acs__order-badge acs__order-badge--none" title="이 후기에 연결된 주문이 없습니다.">
                  <AppIcon icon="mdi-help-circle-outline" :size="14" />
                  연결된 주문 없음
                </span>
                <span v-if="row.orderAt" class="sui-table__sub">
                  {{ String(row.orderAt).slice(0, 10) }}
                </span>
                <span v-if="row.orderSummary" class="sui-table__sub acs__order-summary" :title="row.orderSummary">
                  {{ row.orderSummary }}
                </span>
              </td>
              <td>
                <span class="acs__body">{{ row.content }}</span>
                <span v-if="row.reply" class="sui-table__sub acs__reply">A. {{ row.reply }}</span>
              </td>
              <td class="is-center">
                <a
                  class="sui-btn sui-btn--sm"
                  :href="`/mall/${encodeURIComponent(row.productCode)}`"
                  target="_blank"
                  rel="noopener"
                >
                  상품
                </a>
                <button
                  type="button"
                  class="sui-btn sui-btn--sm"
                  :disabled="working === row.id"
                  @click="onHide('review', row.idx, row.id)"
                >
                  숨김
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="sui-empty">
        <AppIcon icon="mdi-message-text-outline" :size="32" />
        <p>{{ loading ? '불러오는 중입니다…' : '조건에 맞는 항목이 없습니다.' }}</p>
      </div>
    </section>

    <div
      v-if="consultDetail"
      class="acs__modal"
      data-tour="admin-cs-consult-detail"
      @click.self="consultDetail = null"
    >
      <div class="acs__panel">
        <header class="acs__panel-head">
          <h2>
            상담요청 상세
            <em
              :class="
                consultDetail.request.status === 'DONE'
                  ? 'is-done'
                  : consultDetail.request.sellerReplyAllowed
                    ? 'is-delegated'
                    : 'is-wait'
              "
            >
              {{ consultStatusLabel(consultDetail.request) }}
            </em>
          </h2>
          <button type="button" class="acs__close" aria-label="닫기" @click="consultDetail = null">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="acs__panel-body">
          <dl class="acs__meta">
            <div>
              <dt>분류</dt>
              <dd>{{ consultDetail.request.kind || '일반상담' }}</dd>
            </div>
            <div>
              <dt>제목</dt>
              <dd>{{ consultDetail.request.title }}</dd>
            </div>
            <div>
              <dt>작성자 ID</dt>
              <dd>{{ consultDetail.request.authorId }}</dd>
            </div>
            <div>
              <dt>채널</dt>
              <dd>
                <span
                  class="acs__channel-badge"
                  :class="consultDetail.request.channel === 'MALL' ? 'is-mall' : 'is-portal'"
                >
                  {{ consultDetail.request.channel === 'MALL' ? '함우리몰' : '포털' }}
                </span>
                <span v-if="consultDetail.request.isTest" class="acs__channel-badge is-test">테스트</span>
              </dd>
            </div>
            <div>
              <dt>기관명</dt>
              <dd>{{ consultDetail.request.orgName || '-' }}</dd>
            </div>
            <div>
              <dt>기관기호</dt>
              <dd>{{ consultDetail.request.orgCode || '-' }}</dd>
            </div>
            <div>
              <dt>관련 주문</dt>
              <dd>
                <router-link
                  v-if="consultDetail.request.orderCode"
                  class="acs__order-badge"
                  :to="{ path: '/admin/orders', query: { keyword: consultDetail.request.orderCode } }"
                >
                  <AppIcon icon="mdi-receipt-text-outline" :size="14" />
                  <span class="acs__order-badge-code">{{ consultDetail.request.orderCode }}</span>
                  <AppIcon icon="mdi-chevron-right" :size="14" />
                </router-link>
                <span v-else>{{ consultDetail.request.channel === 'MALL' ? '주문 없는 몰 문의' : '포털 상담' }}</span>
              </dd>
            </div>
            <div>
              <dt>연락처</dt>
              <dd>{{ consultDetail.request.contactPhone || '-' }}</dd>
            </div>
            <div>
              <dt>등록일</dt>
              <dd>{{ formatDate(consultDetail.request.createdAt) }}</dd>
            </div>
          </dl>

          <section class="acs__block">
            <h3>상담 내용</h3>
            <p class="acs__block-text">{{ consultDetail.request.content }}</p>
          </section>

          <section v-if="consultDetail.request.hasExcel" class="acs__block">
            <h3>첨부 Excel</h3>
            <button
              type="button"
              class="sui-btn sui-btn--sm"
              :disabled="downloadingExcel === 'req'"
              @click="onDownloadConsultExcel"
            >
              {{ consultDetail.request.excelName || 'Excel 다운로드' }}
            </button>
          </section>

          <section class="acs__block acs__delegate" data-tour="admin-cs-consult-delegate">
            <h3>판매업체에게 답변 위임</h3>
            <p class="acs__delegate-hint">
              {{
                consultDetail.request.orderCode
                  ? '위임을 켜면 이 주문의 판매업체가 판매관리센터에서 직접 답변을 남길 수 있습니다. 언제든 다시 회수할 수 있습니다.'
                  : '관련 주문이 없는 일반 상담은 위임할 수 없습니다. 관리자만 답변합니다.'
              }}
            </p>
            <div class="acs__delegate-row">
              <label class="acs__switch">
                <input
                  type="checkbox"
                  :checked="consultDetail.request.sellerReplyAllowed === true"
                  :disabled="
                    delegateSaving ||
                    !consultDetail.request.orderCode
                  "
                  @change="onToggleSellerReply(($event.target as HTMLInputElement).checked)"
                />
                <span class="acs__switch-track"><span class="acs__switch-thumb" /></span>
                <span class="acs__switch-label">
                  {{
                    consultDetail.request.sellerReplyAllowed
                      ? '판매업체 답변 허용'
                      : '판매업체 답변 잠금'
                  }}
                </span>
              </label>
              <span v-if="delegateSaving" class="acs__delegate-status">저장 중…</span>
              <span
                v-else-if="consultDetail.request.sellerReplyAllowed && consultDetail.request.delegatedAt"
                class="acs__delegate-status"
              >
                위임: {{ formatDate(consultDetail.request.delegatedAt) }}
                <template v-if="consultDetail.request.delegatedBy">
                  · {{ consultDetail.request.delegatedBy }}
                </template>
              </span>
            </div>
          </section>

          <section v-if="consultDetail.responses.length" class="acs__block">
            <h3>등록된 답변</h3>
            <article
              v-for="response in consultDetail.responses"
              :key="response.id"
              class="acs__reply-card"
              :class="{ 'is-seller': response.role === 'SELLER' }"
            >
              <header>
                <strong>{{ response.byName || (response.role === 'SELLER' ? '판매업체' : '관리자') }}</strong>
                <em
                  class="acs__reply-role"
                  :class="response.role === 'SELLER' ? 'is-seller' : 'is-admin'"
                >
                  {{ response.role === 'SELLER' ? '판매자' : '관리자' }}
                </em>
                <time>{{ formatDate(response.createdAt) }}</time>
              </header>
              <p>{{ response.content }}</p>
              <button
                v-if="response.hasExcel"
                type="button"
                class="sui-btn sui-btn--sm"
                :disabled="downloadingExcel === `res-${response.id}`"
                @click="onDownloadConsultResponseExcel(response)"
              >
                {{ response.excelName || 'Excel 다운로드' }}
              </button>
            </article>
          </section>

          <section class="acs__block" data-tour="admin-cs-consult-reply">
            <h3>답변 작성</h3>
            <div class="acs__suggest" data-tour="admin-cs-consult-suggest">
              <div class="acs__suggest-head">
                <span class="acs__suggest-label">
                  {{
                    aiSuggestSource === 'ai'
                      ? 'AI 답변 추천'
                      : aiSuggestLoading
                        ? 'AI 분석 중'
                        : '답변 추천'
                  }}
                </span>
                <button
                  type="button"
                  class="sui-btn sui-btn--sm"
                  :disabled="aiSuggestLoading || !consultDetail"
                  @click="loadAiSuggestions(true)"
                >
                  {{ aiSuggestLoading ? '생성 중…' : '다시 생성' }}
                </button>
              </div>
              <p v-if="aiSuggestHint" class="acs__suggest-hint">{{ aiSuggestHint }}</p>
              <p v-if="aiSuggestLoading && !replySuggestions.length" class="acs__suggest-hint">
                문의 내용을 분석해 답변 초안을 만드는 중입니다…
              </p>
              <button
                v-for="(item, index) in replySuggestions"
                :key="index"
                type="button"
                class="acs__suggest-btn"
                @click="applyReplySuggestion(item)"
              >
                {{ item }}
              </button>
            </div>
            <textarea
              v-model="replyContent"
              class="acs__textarea"
              rows="6"
              placeholder="상담 답변 내용을 입력해 주세요."
            />
            <div class="acs__file">
              <span>Excel 첨부 (선택)</span>
              <input
                ref="replyExcelInput"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                @change="onReplyExcelChange"
              />
              <p class="acs__file-hint">.xlsx, 5MB 이하 · 매크로 포함 파일 불가</p>
              <p v-if="replyExcelFile" class="acs__file-name">
                {{ replyExcelFile.name }}
                <button type="button" class="sui-btn sui-btn--sm" @click="clearReplyExcel">취소</button>
              </p>
            </div>
          </section>
        </div>

        <footer class="acs__panel-foot">
          <button type="button" class="sui-btn" @click="consultDetail = null">닫기</button>
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="submittingReply || !replyContent.trim()"
            @click="submitConsultReply"
          >
            답변 등록
          </button>
        </footer>
      </div>
    </div>

    <div
      v-if="qnaReply"
      class="acs__modal"
      data-tour="admin-cs-qna-reply"
      @click.self="closeQnaReply"
    >
      <div class="acs__panel">
        <header class="acs__panel-head">
          <h2>
            상품문의 답변
            <em class="is-wait">미답변</em>
          </h2>
          <button type="button" class="acs__close" aria-label="닫기" @click="closeQnaReply">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="acs__panel-body">
          <dl class="acs__meta">
            <div>
              <dt>상품</dt>
              <dd>{{ qnaReply.productName || qnaReply.productCode }}</dd>
            </div>
            <div>
              <dt>판매업체</dt>
              <dd>{{ qnaReply.storeName || '-' }}</dd>
            </div>
            <div>
              <dt>기관</dt>
              <dd>{{ qnaReply.org || '-' }}</dd>
            </div>
            <div>
              <dt>작성자</dt>
              <dd>{{ qnaReply.author || '-' }}</dd>
            </div>
            <div>
              <dt>관련 주문</dt>
              <dd>{{ qnaReply.orderCode || '일반 문의' }}</dd>
            </div>
            <div>
              <dt>등록일</dt>
              <dd>{{ qnaReply.date || '-' }}</dd>
            </div>
          </dl>

          <section class="acs__block">
            <h3>문의 내용</h3>
            <p class="acs__block-text">{{ qnaReply.question }}</p>
          </section>

          <section class="acs__block">
            <h3>답변 작성</h3>
            <p v-if="errorMessage" class="acs__error">
              <AppIcon icon="mdi-alert-outline" :size="16" />
              {{ errorMessage }}
            </p>
            <textarea
              v-model="qnaReplyContent"
              class="acs__textarea"
              rows="6"
              placeholder="판매업체를 대신해 답변할 내용을 입력해 주세요."
            />
          </section>
        </div>

        <footer class="acs__panel-foot">
          <button type="button" class="sui-btn" @click="closeQnaReply">닫기</button>
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="submittingQnaReply || !qnaReplyContent.trim()"
            @click="submitQnaReply"
          >
            {{ submittingQnaReply ? '등록 중…' : '답변 등록' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  answerAdminCsQna,
  createAdminConsultResponse,
  deleteAdminCsItem,
  downloadAdminConsultExcel,
  downloadAdminConsultResponseExcel,
  fetchAdminConsultAiReplies,
  fetchAdminConsultDetail,
  fetchAdminConsultList,
  fetchAdminCs,
  setAdminConsultSellerReplyPermission,
  type AdminConsultDetail,
  type AdminConsultListItem,
  type AdminConsultResponse,
  type AdminCsQna,
  type AdminCsReview,
} from '@/api/admin'

type CsTab = 'consult' | 'qna' | 'review'

const route = useRoute()
const router = useRouter()

function initialTab(): CsTab {
  const raw = String(route.query.tab || '')
  if (raw === 'qna' || raw === 'review' || raw === 'consult') return raw
  return 'consult'
}

const tab = ref<CsTab>(initialTab())
const unansweredOnly = ref(route.query.unanswered === '1')
const keyword = ref(String(route.query.keyword || ''))
const consultStatus = ref(String(route.query.status || ''))
const consultChannel = ref(String(route.query.channel || '').toUpperCase() === 'MALL' ? 'MALL' : String(route.query.channel || '').toUpperCase() === 'PORTAL' ? 'PORTAL' : '')
const qnas = ref<AdminCsQna[]>([])
const reviews = ref<AdminCsReview[]>([])
const consultItems = ref<AdminConsultListItem[]>([])
const consultPagination = ref({ page: 1, limit: 20, total: 0, totalPages: 1 })
const unansweredQna = ref(0)
const unansweredReview = ref(0)
const unansweredConsult = ref(0)
const unansweredConsultPortal = ref(0)
const unansweredConsultMall = ref(0)
const loading = ref(false)
const working = ref('')
const errorMessage = ref('')
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined

const replySuggestions = ref<string[]>([])
const aiSuggestLoading = ref(false)
const aiSuggestSource = ref<'ai' | 'fallback' | ''>('')
const aiSuggestHint = ref('')
let aiSuggestSeq = 0

const consultDetail = ref<AdminConsultDetail | null>(null)
const replyContent = ref('')
const replyExcelFile = ref<File | null>(null)
const replyExcelInput = ref<HTMLInputElement | null>(null)
const submittingReply = ref(false)
const delegateSaving = ref(false)
const downloadingExcel = ref('')

const qnaReply = ref<AdminCsQna | null>(null)
const qnaReplyContent = ref('')
const submittingQnaReply = ref(false)

const viewCount = computed(() => {
  if (tab.value === 'consult') return consultItems.value.length
  if (tab.value === 'qna') return qnas.value.length
  return reviews.value.length
})

function notify(message: string) {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => {
    toast.value = ''
  }, 2400)
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ko-KR')
}

function consultStatusLabel(row: { status?: string; sellerReplyAllowed?: boolean }) {
  const base = row.status === 'DONE' ? '답변완료' : '답변대기'
  return row.sellerReplyAllowed ? `${base} · 위임` : base
}

function consultStatusBadgeClass(row: { status?: string; sellerReplyAllowed?: boolean }) {
  if (row.status === 'DONE') return 'sui-badge--green'
  if (row.sellerReplyAllowed) return 'sui-badge--blue'
  return 'sui-badge--amber'
}

function setTab(next: CsTab) {
  tab.value = next
  syncQuery()
  void (next === 'consult' ? loadConsult(1) : load())
}

function syncQuery() {
  const query: Record<string, string> = {}
  if (tab.value !== 'consult') query.tab = tab.value
  if (tab.value !== 'consult' && unansweredOnly.value) query.unanswered = '1'
  if (tab.value === 'consult' && consultStatus.value) query.status = consultStatus.value
  if (tab.value === 'consult' && consultChannel.value) query.channel = consultChannel.value
  if (keyword.value.trim()) query.keyword = keyword.value.trim()
  router.replace({ path: '/admin/cs', query })
}

function setConsultChannel(next: string) {
  if (consultChannel.value === next) return
  consultChannel.value = next
  syncQuery()
  void loadConsult(1)
}

async function loadConsult(page = consultPagination.value.page) {
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await fetchAdminConsultList({
      page,
      limit: consultPagination.value.limit,
      keyword: keyword.value,
      status: consultStatus.value,
      channel: consultChannel.value,
    })
    consultItems.value = result.items
    consultPagination.value = result.pagination
    unansweredConsult.value = result.unanswered
    unansweredConsultPortal.value = result.unansweredPortal ?? 0
    unansweredConsultMall.value = result.unansweredMall ?? 0
  } catch (error) {
    consultItems.value = []
    errorMessage.value = error instanceof Error ? error.message : '상담요청을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await fetchAdminCs({
      tab: tab.value === 'review' ? 'review' : 'qna',
      unanswered: unansweredOnly.value,
      keyword: keyword.value,
    })
    qnas.value = result.qnas
    reviews.value = result.reviews
    unansweredQna.value = result.unansweredQna
    unansweredReview.value = result.unansweredReview
  } catch (error) {
    qnas.value = []
    reviews.value = []
    errorMessage.value = error instanceof Error ? error.message : '문의·후기를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function refreshSideCounts() {
  try {
    const [cs, consult] = await Promise.all([
      fetchAdminCs({ tab: 'qna' }),
      fetchAdminConsultList({ page: 1, limit: 1 }),
    ])
    unansweredQna.value = cs.unansweredQna
    unansweredReview.value = cs.unansweredReview
    unansweredConsult.value = consult.unanswered
    unansweredConsultPortal.value = consult.unansweredPortal ?? 0
    unansweredConsultMall.value = consult.unansweredMall ?? 0
  } catch {
    /* ignore */
  }
}

async function openConsultDetail(id: number) {
  try {
    consultDetail.value = await fetchAdminConsultDetail(id)
    replyContent.value = ''
    clearReplyExcel()
    void loadAiSuggestions()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '상담 상세를 불러오지 못했습니다.'
  }
}

async function loadAiSuggestions(force = false) {
  if (!consultDetail.value) return
  if (aiSuggestLoading.value && !force) return

  const seq = ++aiSuggestSeq
  const requestId = consultDetail.value.request.id
  aiSuggestLoading.value = true
  aiSuggestHint.value = ''
  if (force) replySuggestions.value = []

  try {
    const result = await fetchAdminConsultAiReplies(requestId)
    if (seq !== aiSuggestSeq) return
    replySuggestions.value = result.suggestions
    aiSuggestSource.value = result.source
    aiSuggestHint.value =
      result.source === 'ai'
        ? '문의 내용을 분석한 AI 초안입니다. 클릭하면 입력란에 들어갑니다.'
        : result.message || '자동 초안입니다. OpenAI API 키를 설정하면 AI 추천이 활성화됩니다.'
  } catch (error) {
    if (seq !== aiSuggestSeq) return
    replySuggestions.value = ['기능 오류 수정 완료입니다. 이용에 불편을 드려 죄송합니다.']
    aiSuggestSource.value = 'fallback'
    aiSuggestHint.value = error instanceof Error ? error.message : 'AI 추천을 불러오지 못했습니다.'
  } finally {
    if (seq === aiSuggestSeq) aiSuggestLoading.value = false
  }
}

function onReplyExcelChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] || null
  if (!file) {
    replyExcelFile.value = null
    return
  }
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    errorMessage.value = 'Excel(.xlsx) 파일만 업로드할 수 있습니다.'
    input.value = ''
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = '파일 크기는 5MB 이하여야 합니다.'
    input.value = ''
    return
  }
  replyExcelFile.value = file
}

function clearReplyExcel() {
  replyExcelFile.value = null
  if (replyExcelInput.value) replyExcelInput.value.value = ''
}

function applyReplySuggestion(text: string) {
  replyContent.value = text
}

async function onToggleSellerReply(next: boolean) {
  if (!consultDetail.value) return
  if (delegateSaving.value) return
  if (consultDetail.value.request.sellerReplyAllowed === next) return
  /*
   * 관리자가 「판매업체 답변 허용」 을 켜면 이 주문에 실제로 낀 판매업체가
   * 판매관리센터에서 답변할 수 있게 된다. 저장 중에 스위치가 다시 꺼지지
   * 않도록 먼저 화면에 반영하고, 실패하면 되돌린다.
   */
  const reqId = consultDetail.value.request.id
  const prev = consultDetail.value.request.sellerReplyAllowed === true
  consultDetail.value.request.sellerReplyAllowed = next
  delegateSaving.value = true
  errorMessage.value = ''
  try {
    const saved = await setAdminConsultSellerReplyPermission(reqId, next)
    const allowed = saved.sellerReplyAllowed === true
    if (consultDetail.value?.request.id === reqId) {
      consultDetail.value.request.sellerReplyAllowed = allowed
    }
    notify(allowed ? '판매업체에게 답변 권한을 위임했습니다.' : '판매업체 답변 권한을 회수했습니다.')
    await openConsultDetail(reqId)
    await loadConsult()
  } catch (error) {
    if (consultDetail.value?.request.id === reqId) {
      consultDetail.value.request.sellerReplyAllowed = prev
    }
    errorMessage.value = error instanceof Error ? error.message : '위임 상태 변경에 실패했습니다.'
  } finally {
    delegateSaving.value = false
  }
}

async function submitConsultReply() {
  if (!consultDetail.value || !replyContent.value.trim() || submittingReply.value) return
  submittingReply.value = true
  errorMessage.value = ''
  try {
    await createAdminConsultResponse(
      consultDetail.value.request.id,
      replyContent.value.trim(),
      replyExcelFile.value,
    )
    notify('답변이 등록되었습니다.')
    await openConsultDetail(consultDetail.value.request.id)
    await loadConsult()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '답변 등록에 실패했습니다.'
  } finally {
    submittingReply.value = false
  }
}

async function onDownloadConsultExcel() {
  if (!consultDetail.value) return
  downloadingExcel.value = 'req'
  try {
    await downloadAdminConsultExcel(
      consultDetail.value.request.id,
      consultDetail.value.request.excelName || `consult-${consultDetail.value.request.id}.xlsx`,
    )
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Excel 다운로드에 실패했습니다.'
  } finally {
    downloadingExcel.value = ''
  }
}

async function onDownloadConsultResponseExcel(response: AdminConsultResponse) {
  if (!consultDetail.value) return
  downloadingExcel.value = `res-${response.id}`
  try {
    await downloadAdminConsultResponseExcel(
      consultDetail.value.request.id,
      response.id,
      response.excelName || `consult-response-${response.id}.xlsx`,
    )
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Excel 다운로드에 실패했습니다.'
  } finally {
    downloadingExcel.value = ''
  }
}

function openQnaReply(row: AdminCsQna) {
  qnaReply.value = row
  qnaReplyContent.value = ''
  errorMessage.value = ''
}

function closeQnaReply() {
  qnaReply.value = null
  qnaReplyContent.value = ''
}

async function submitQnaReply() {
  if (!qnaReply.value || !qnaReplyContent.value.trim() || submittingQnaReply.value) return
  submittingQnaReply.value = true
  errorMessage.value = ''
  try {
    await answerAdminCsQna(qnaReply.value.idx, qnaReplyContent.value.trim())
    notify('문의 답변을 등록했습니다.')
    closeQnaReply()
    await load()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '문의 답변 등록에 실패했습니다.'
  } finally {
    submittingQnaReply.value = false
  }
}

async function onHide(kind: 'qna' | 'review', idx: number, id: string) {
  if (working.value) return
  if (!window.confirm('몰에서 숨길까요? 판매업체·구매 기관 목록에서도 보이지 않습니다.')) return
  working.value = id
  try {
    await deleteAdminCsItem(kind, idx)
    notify('숨김 처리했습니다.')
    await load()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '숨김 처리에 실패했습니다.'
  } finally {
    working.value = ''
  }
}

watch(unansweredOnly, () => {
  if (tab.value !== 'consult') syncQuery()
})

watch(consultStatus, () => {
  if (tab.value === 'consult') syncQuery()
})

onMounted(async () => {
  await refreshSideCounts()
  if (tab.value === 'consult') await loadConsult(1)
  else await load()
})
</script>

<style lang="scss" src="./AdminCsPage.scss"></style>
